// @ts-nocheck
import React, { useState, useEffect } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity, StyleSheet, Platform, TextInput,
} from 'react-native';
import { Colors, FontSize, FontWeight, Radii, Shadow } from '../../theme/tokens';

// Web-only staggered fade-up entrance.
function fadeUp(delayMs: number): any {
  if (Platform.OS !== 'web') return null;
  return {
    animationKeyframes: { '0%': { opacity: 0, transform: [{ translateY: 14 }] }, '100%': { opacity: 1, transform: [{ translateY: 0 }] } },
    animationDuration: '500ms', animationDelay: `${delayMs}ms`, animationFillMode: 'both',
    animationTimingFunction: 'cubic-bezier(.2,.7,.3,1)',
  };
}
import { useResponsive } from '../../theme/responsive';
import { useApp } from '../../store/AppStore';
import { ScreenShell } from '../../components/WebShell';
import ConfirmDialog from '../../components/ConfirmDialog';
import SuccessDialog from '../../components/SuccessDialog';
import { Icon } from '../../components/Icon';
import PrimaryButton from '../../components/Button_Component/Primary_Button';
import { Chip } from '../../components/Chip';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function maskToken(token: string): string {
  if (!token) return '••••••••••••••••••••';
  return token.slice(0, 8) + '·'.repeat(20) + token.slice(-4);
}

// Icon-led section header.
function SectionH({ icon, title, sub, first }: { icon: string; title: string; sub: string; first?: boolean }) {
  return (
    <View style={[styles.sectionH, !first && { marginTop: 40 }]}>
      <View style={styles.sectionHRow}>
        <View style={styles.sectionIcon}>
          <Icon name={icon as any} size={14} color={Colors.accent} stroke={1.9} />
        </View>
        <Text style={styles.sectionTitle}>{title}</Text>
      </View>
      <Text style={styles.sectionSub}>{sub}</Text>
    </View>
  );
}

// ─── New Token Modal ──────────────────────────────────────────────────────────

function NewTokenModal({ onClose, onCreate }: {
  onClose: () => void;
  onCreate: (name: string, scope: string, env: string) => void;
}) {
  const [name, setName] = useState('');
  const [scope, setScope] = useState('read+write');
  const [env, setEnv] = useState('live');

  return (
    <TouchableOpacity style={styles.overlay} onPress={onClose} activeOpacity={1}>
      <TouchableOpacity style={styles.modal} activeOpacity={1} onPress={() => {}}>
        <View style={styles.modalHead}>
          <Text style={styles.modalTitle}>Generate API token</Text>
          <TouchableOpacity onPress={onClose}>
            <Icon name="X" size={14} color={Colors.ink3} />
          </TouchableOpacity>
        </View>
        <View style={styles.modalBody}>
          <View style={styles.field}>
            <Text style={styles.fieldLabel}>Token name</Text>
            <TextInput
              style={styles.input}
              value={name}
              onChangeText={setName}
              placeholder="e.g. Production · backend service"
              autoFocus
            />
            <Text style={styles.fieldHint}>For your own bookkeeping. Shown in audit logs.</Text>
          </View>
          <View style={styles.field}>
            <Text style={styles.fieldLabel}>Scope</Text>
            <View style={styles.pickerRow}>
              {[['read', 'Read-only', 'GET endpoints only'], ['read+write', 'Read + write', 'Full API access']].map(([val, title, sub]) => (
                <TouchableOpacity
                  key={val}
                  style={[styles.pickerCard, scope === val && styles.pickerCardActive]}
                  onPress={() => setScope(val)}
                >
                  <Text style={[styles.pickerTitle, scope === val && styles.pickerTitleActive]}>{title}</Text>
                  <Text style={styles.pickerSub}>{sub}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
          <View style={styles.field}>
            <Text style={styles.fieldLabel}>Environment</Text>
            <View style={styles.pickerRow}>
              {[['live', 'Live', 'sk_live_… · production'], ['test', 'Test', 'sk_test_… · sandbox']].map(([val, title, sub]) => (
                <TouchableOpacity
                  key={val}
                  style={[styles.pickerCard, env === val && styles.pickerCardActive]}
                  onPress={() => setEnv(val)}
                >
                  <Text style={[styles.pickerTitle, env === val && styles.pickerTitleActive]}>{title}</Text>
                  <Text style={styles.pickerSub}>{sub}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
          <View style={styles.warnBox}>
            <Icon name="Alert" size={13} color={Colors.warn} />
            <Text style={styles.warnText}>The token will be shown <Text style={{ fontWeight: FontWeight.semibold }}>once</Text> after creation. Copy it immediately — we don't store the plaintext.</Text>
          </View>
        </View>
        <View style={styles.modalFoot}>
          <PrimaryButton title="Cancel" size="md" variant="default" onPress={onClose} />
          <PrimaryButton
            title="Generate token"
            size="md"
            variant="primary"
            leftIcon={<Icon name="Check" size={13} color={Colors.paper} />}
            disabled={!name.trim()}
            onPress={() => onCreate(name.trim(), scope, env)}
          />
        </View>
      </TouchableOpacity>
    </TouchableOpacity>
  );
}

// ─── ApiScreen ────────────────────────────────────────────────────────────────

export function ApiScreen() {
  const {
    toast, copy,
    apiTokens, loadApiTokens, createApiToken, revokeApiToken, updateApiToken,
    webhooks, loadWebhooks, createWebhook, updateWebhook, deleteWebhook, testWebhook, loadSigningSecret,
    rotateSigningSecret,
  } = useApp();

  // Edit / delete → confirm → success flows for tokens & webhooks.
  const [tokenEdit, setTokenEdit] = useState<{ id: string; name: string } | null>(null);
  const [tokenEditConfirm, setTokenEditConfirm] = useState(false);
  const [tokenDelete, setTokenDelete] = useState<any | null>(null);
  const [whEdit, setWhEdit] = useState<{ id: string; url: string } | null>(null);
  const [whEditConfirm, setWhEditConfirm] = useState(false);
  const [whDelete, setWhDelete] = useState<any | null>(null);
  const [rowBusy, setRowBusy] = useState(false);
  const [rowSuccess, setRowSuccess] = useState<string | null>(null);

  const submitTokenEdit = async () => {
    if (!tokenEdit) return;
    const name = tokenEdit.name.trim();
    if (!name) { setTokenEdit(null); return; }
    setRowBusy(true);
    try { await updateApiToken(tokenEdit.id, name); setTokenEditConfirm(false); setTokenEdit(null); setRowSuccess(`Token renamed to "${name}".`); }
    catch { toast('Failed to rename token.', 'danger'); }
    finally { setRowBusy(false); }
  };
  const confirmTokenDelete = async () => {
    if (!tokenDelete) return;
    setRowBusy(true);
    try { const n = tokenDelete.name; await revokeApiToken(tokenDelete.id); setTokenDelete(null); setRowSuccess(`Token "${n}" revoked.`); }
    catch { toast('Failed to revoke token.', 'danger'); }
    finally { setRowBusy(false); }
  };
  const submitWhEdit = async () => {
    if (!whEdit) return;
    const url = whEdit.url.trim();
    if (!url.startsWith('http')) { toast('Enter a valid https URL.', 'warn'); return; }
    setRowBusy(true);
    try { await updateWebhook(whEdit.id, { url }); setWhEditConfirm(false); setWhEdit(null); setRowSuccess('Webhook endpoint updated.'); }
    catch { toast('Failed to update webhook.', 'danger'); }
    finally { setRowBusy(false); }
  };
  const confirmWhDelete = async () => {
    if (!whDelete) return;
    setRowBusy(true);
    try { await deleteWebhook(whDelete.id); setWhDelete(null); setRowSuccess('Webhook endpoint deleted.'); }
    catch { toast('Failed to delete webhook.', 'danger'); }
    finally { setRowBusy(false); }
  };

  const { isMobile, pagePadX } = useResponsive();

  const [revealedIds, setRevealedIds] = useState<Set<string>>(new Set());
  const [hoverRow, setHoverRow] = useState<string | null>(null);
  const [newWebhookUrl, setNewWebhookUrl] = useState('');
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [justCreated, setJustCreated] = useState<{ name: string; token: string } | null>(null);
  const [signingSecret, setSigningSecret] = useState<string | null>(null);
  const [editingWebhookId, setEditingWebhookId] = useState<string|null>(null);
  const [editWebhookUrl, setEditWebhookUrl] = useState('');

  useEffect(() => {
    loadApiTokens();
    loadWebhooks();
    loadSigningSecret().then(s => setSigningSecret(s));
  }, []);

  const handleCreateToken = async (name: string, scope: string, env: string) => {
    const token = await createApiToken({ name, scope, env });
    setShowTokenModal(false);
    if (token) {
      const rawToken = token.token || token.rawToken || token.plaintext || '';
      if (rawToken) {
        setJustCreated({ name, token: rawToken });
        setRevealedIds(ids => new Set([...ids, token.id]));
      }
    }
  };

  const handleAddWebhook = async () => {
    if (!newWebhookUrl.startsWith('http')) return;
    await createWebhook({ url: newWebhookUrl, events: ['ticket.created'] });
    setNewWebhookUrl('');
  };

  const formatDate = (d: string | null | undefined) => {
    if (!d) return 'never';
    try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return d; }
  };

  // On mobile the fixed-width table columns can overflow; wrap each table in a
  // horizontal scroll so the layout never breaks. On wider screens render directly.
  const TableWrap = isMobile
    ? (props: any) => (
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ minWidth: 720 }}>
          {props.children}
        </ScrollView>
      )
    : React.Fragment;

  return (
    <ScreenShell crumbs={[
      { label: 'Settings' },
      { label: 'API & webhooks' },
    ]}>
      <View style={styles.root}>
        <View style={[styles.pageHead, { paddingHorizontal: pagePadX, flexWrap: 'wrap' }]}>
          <View style={{ flex: 1 }}>
            <Text style={styles.pageTitle}>API & webhooks</Text>
            <Text style={styles.pageSub}>Programmatic access to sk-tickets. Generate scoped tokens and subscribe to events via webhook.</Text>
          </View>
        </View>

        <ScrollView style={styles.body} contentContainerStyle={[styles.bodyContent, { padding: isMobile ? 16 : 32, paddingTop: isMobile ? 16 : 28 }]}>
          {/* Just-created banner */}
          {justCreated && (
            <View style={styles.justCreatedBanner}>
              <Icon name="Eye" size={16} color={Colors.warn} />
              <View style={{ flex: 1 }}>
                <Text style={styles.justCreatedTitle}>Copy this token now — it won't be shown again.</Text>
                <View style={styles.justCreatedTokenRow}>
                  <Text style={styles.justCreatedToken} numberOfLines={1}>{justCreated.token}</Text>
                  <PrimaryButton
                    title="Copy"
                    size="sm"
                    variant="default"
                    leftIcon={<Icon name="Copy" size={12} color={Colors.ink} />}
                    onPress={() => copy(justCreated.token, justCreated.name)}
                  />
                  <PrimaryButton title="Dismiss" size="sm" variant="ghost" onPress={() => setJustCreated(null)} />
                </View>
              </View>
            </View>
          )}

          {/* API Tokens */}
          <SectionH first icon="Server" title="API tokens" sub="Use as a Bearer token in the Authorization header. Keep secret — rotate if leaked." />

          <TableWrap>
          <View style={[styles.card, { marginTop: 14 }, fadeUp(40)]}>
            <View style={[styles.tableRow, styles.tableHeader]}>
              <Text style={[styles.thText, { flex: 2 }]}>Name</Text>
              <Text style={[styles.thText, { width: 90 }]}>Scope</Text>
              <Text style={[styles.thText, { flex: 3 }]}>Token</Text>
              <Text style={[styles.thText, { width: 110 }]}>Created</Text>
              <Text style={[styles.thText, { width: 100 }]}>Last used</Text>
              <Text style={[styles.thText, { width: 132, textAlign: 'right' }]}>Actions</Text>
            </View>
            {apiTokens.length === 0 && (
              <View style={styles.emptyMini}>
                <View style={styles.emptyMiniIcon}><Icon name="Server" size={16} color={Colors.accent} stroke={1.8} /></View>
                <Text style={styles.emptyMiniText}>No API tokens yet — generate one to start building.</Text>
              </View>
            )}
            {apiTokens.map((t, i) => {
              const revealed = revealedIds.has(t.id);
              const displayToken = revealed ? (t.token || t.rawToken || '') : maskToken(t.token || '');
              return (
                <View key={t.id}
                  {...(Platform.OS === 'web' ? { onMouseEnter: () => setHoverRow(t.id), onMouseLeave: () => setHoverRow(null) } as any : {})}
                  style={[styles.tableRow, i < apiTokens.length - 1 && styles.rowBorder, hoverRow === t.id && styles.rowHover]}>
                  <Text style={[styles.cellText, { flex: 2 }]}>{t.name}</Text>
                  <View style={{ width: 90 }}>
                    <Chip label={t.scope || 'read+write'} tone={t.scope === 'read' ? 'neutral' : 'accent'} size="sm" />
                  </View>
                  <View style={{ flex: 3 }}>
                    <Text style={styles.tokenText} numberOfLines={1}>{displayToken}</Text>
                  </View>
                  <Text style={[styles.metaText, { width: 110 }]}>{formatDate(t.createdAt)}</Text>
                  <Text style={[styles.metaText, { width: 100 }]}>{t.lastUsed ? formatDate(t.lastUsed) : 'never'}</Text>
                  <View style={[styles.tokenActions, { width: 132 }]}>
                    <TouchableOpacity
                      style={styles.iconBtn}
                      onPress={() => setRevealedIds(ids => {
                        const next = new Set(ids);
                        next.has(t.id) ? next.delete(t.id) : next.add(t.id);
                        return next;
                      })}
                    >
                      <Icon name="Eye" size={12} color={Colors.ink3} />
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.iconBtn}
                      onPress={() => copy(t.token || '', t.name)}
                    >
                      <Icon name="Copy" size={12} color={Colors.ink3} />
                    </TouchableOpacity>
                    <TouchableOpacity style={styles.iconBtn} onPress={() => setTokenEdit({ id: t.id, name: t.name })} accessibilityLabel={`Rename token ${t.name}`}>
                      <Icon name="Edit" size={12} color={Colors.ink3} />
                    </TouchableOpacity>
                    <TouchableOpacity style={styles.iconBtn} onPress={() => setTokenDelete(t)} accessibilityLabel={`Revoke token ${t.name}`}>
                      <Icon name="Trash" size={12} color={Colors.ink3} />
                    </TouchableOpacity>
                  </View>
                </View>
              );
            })}
            <View style={styles.tableFooter}>
              <Text style={styles.footerHint}>
                {apiTokens.length} active {apiTokens.length === 1 ? 'token' : 'tokens'} · the secret is shown only once at creation
              </Text>
              <PrimaryButton
                title="New token"
                variant="primary"
                size="md"
                leftIcon={<Icon name="Plus" size={13} color={Colors.paper} />}
                onPress={() => setShowTokenModal(true)}
              />
            </View>
          </View>
          </TableWrap>

          {/* Webhooks */}
          <SectionH icon="Send" title="Webhooks" sub="Receive POST events when something changes. We retry failures up to 5 times with exponential backoff." />

          <View style={[styles.card, { marginTop: 14 }, fadeUp(110)]}>
            {webhooks.length === 0 && (
              <View style={styles.emptyMini}>
                <View style={styles.emptyMiniIcon}><Icon name="Send" size={16} color={Colors.accent} stroke={1.8} /></View>
                <Text style={styles.emptyMiniText}>No webhooks configured yet — add an endpoint below to receive events.</Text>
              </View>
            )}
            {webhooks.map((w, i) => (
              <View key={w.id} style={[styles.webhookRow, i < webhooks.length - 1 && styles.rowBorder]}>
                <View style={{ flex: 1 }}>
                  <View style={styles.webhookTitleRow}>
                    <Text style={styles.webhookUrl} numberOfLines={1}>{w.url}</Text>
                    <Chip label={w.status || 'active'} tone={w.status === 'active' ? 'success' : 'danger'} dot size="sm" />
                  </View>
                  <View style={styles.webhookEvents}>
                    {(w.events || []).map(e => <Chip key={e} label={e} tone="default" size="sm" />)}
                  </View>
                  <Text style={styles.lastDelivery}>Last delivery: <Text style={styles.monoText}>{w.lastDelivery || 'never'}</Text></Text>
                </View>
                <View style={styles.webhookBtns}>
                  <PrimaryButton
                    title="Test"
                    size="sm"
                    variant="default"
                    leftIcon={<Icon name="Send" size={12} color={Colors.ink} />}
                    onPress={() => testWebhook(w.id)}
                  />
                  <PrimaryButton
                    title="Edit"
                    size="sm"
                    variant="default"
                    leftIcon={<Icon name="Edit" size={12} color={Colors.ink} />}
                    onPress={() => setWhEdit({ id: w.id, url: w.url || '' })}
                  />
                  <TouchableOpacity style={styles.iconBtn} onPress={() => setWhDelete(w)} accessibilityLabel="Delete webhook">
                    <Icon name="Trash" size={13} color={Colors.ink3} />
                  </TouchableOpacity>
                </View>
              </View>
            ))}
            <View style={[styles.addWebhookRow, isMobile && { flexDirection: 'column', alignItems: 'stretch' }]}>
              <TextInput
                style={[styles.input, { flex: 1 }]}
                placeholder="https://your-server.com/webhook"
                value={newWebhookUrl}
                onChangeText={setNewWebhookUrl}
                keyboardType="url"
                autoCapitalize="none"
              />
              <PrimaryButton
                title="Add webhook"
                variant="primary"
                size="md"
                leftIcon={<Icon name="Plus" size={13} color={Colors.paper} />}
                disabled={!newWebhookUrl.startsWith('http')}
                onPress={handleAddWebhook}
              />
            </View>
          </View>

          {/* Signing secret */}
          <SectionH icon="Lock" title="Signing secret" sub="Used to verify request authenticity. Compute HMAC-SHA256 of the payload with this key." />

          <View style={[styles.card, { marginTop: 14 }, fadeUp(180)]}>
            <View style={[styles.signingRow, isMobile && { flexDirection: 'column', alignItems: 'stretch' }]}>
              <Text style={styles.signingToken}>
                {signingSecret ? signingSecret : 'whsec_••••••••••••••••••••••••'}
              </Text>
              <PrimaryButton
                title="Copy"
                size="sm"
                variant="default"
                leftIcon={<Icon name="Copy" size={12} color={Colors.ink} />}
                onPress={() => signingSecret ? copy(signingSecret, 'signing secret') : toast('No signing secret available.', 'info')}
              />
              <PrimaryButton
                title="Rotate"
                size="sm"
                variant="default"
                leftIcon={<Icon name="Refresh" size={12} color={Colors.ink} />}
                onPress={async () => {
                  const next = await rotateSigningSecret();
                  if (next) setSigningSecret(next);
                }}
              />
            </View>
          </View>
        </ScrollView>

        {showTokenModal && (
          <NewTokenModal onClose={() => setShowTokenModal(false)} onCreate={handleCreateToken} />
        )}
      </View>

      {/* Rename token → edit card */}
      <ConfirmDialog
        visible={tokenEdit !== null}
        title="Rename token"
        message="Update the token's display name. The secret itself never changes."
        confirmText="Save"
        cancelText="Cancel"
        confirmColor="var(--accent)"
        loading={rowBusy}
        onConfirm={() => { if ((tokenEdit?.name ?? '').trim()) setTokenEditConfirm(true); }}
        onCancel={() => { if (!rowBusy) setTokenEdit(null); }}
        onClose={() => { if (!rowBusy) setTokenEdit(null); }}
      >
        <TextInput
          style={styles.dialogInput}
          value={tokenEdit?.name ?? ''}
          autoFocus
          onChangeText={(v) => setTokenEdit(d => (d ? { ...d, name: v } : d))}
          onSubmitEditing={submitTokenEdit}
          placeholder="Token name"
          placeholderTextColor={Colors.ink4}
        />
      </ConfirmDialog>

      {/* Revoke token → confirm */}
      <ConfirmDialog
        visible={tokenDelete !== null}
        title="Revoke token?"
        message={tokenDelete ? `"${tokenDelete.name}" will stop working immediately. Any integration using it will lose access. This can't be undone.` : ''}
        confirmText="Revoke"
        cancelText="Cancel"
        loading={rowBusy}
        onConfirm={confirmTokenDelete}
        onCancel={() => { if (!rowBusy) setTokenDelete(null); }}
      />

      {/* Edit webhook → edit card */}
      <ConfirmDialog
        visible={whEdit !== null}
        title="Edit webhook"
        message="Update the endpoint URL that receives event payloads."
        confirmText="Save"
        cancelText="Cancel"
        confirmColor="var(--accent)"
        loading={rowBusy}
        onConfirm={() => { if ((whEdit?.url ?? '').trim().startsWith('http')) setWhEditConfirm(true); else toast('Enter a valid https URL.', 'warn'); }}
        onCancel={() => { if (!rowBusy) setWhEdit(null); }}
        onClose={() => { if (!rowBusy) setWhEdit(null); }}
      >
        <TextInput
          style={styles.dialogInput}
          value={whEdit?.url ?? ''}
          autoFocus
          onChangeText={(v) => setWhEdit(d => (d ? { ...d, url: v } : d))}
          onSubmitEditing={submitWhEdit}
          placeholder="https://your-server.com/webhook"
          placeholderTextColor={Colors.ink4}
          autoCapitalize="none"
        />
      </ConfirmDialog>

      {/* Delete webhook → confirm */}
      <ConfirmDialog
        visible={whDelete !== null}
        title="Delete webhook?"
        message={whDelete ? `Stop sending events to ${whDelete.url}? This endpoint will be removed.` : ''}
        confirmText="Delete"
        cancelText="Cancel"
        loading={rowBusy}
        onConfirm={confirmWhDelete}
        onCancel={() => { if (!rowBusy) setWhDelete(null); }}
      />

      {/* Token rename → save confirmation */}
      <ConfirmDialog
        visible={tokenEditConfirm}
        title="Update token name?"
        message={tokenEdit ? `Rename this token to "${(tokenEdit.name || '').trim()}"?` : ''}
        confirmText="Yes, update"
        cancelText="Go back"
        confirmColor="var(--accent)"
        loading={rowBusy}
        onConfirm={submitTokenEdit}
        onCancel={() => { if (!rowBusy) setTokenEditConfirm(false); }}
      />

      {/* Webhook edit → save confirmation */}
      <ConfirmDialog
        visible={whEditConfirm}
        title="Update webhook?"
        message={whEdit ? `Send future events to ${(whEdit.url || '').trim()}?` : ''}
        confirmText="Yes, update"
        cancelText="Go back"
        confirmColor="var(--accent)"
        loading={rowBusy}
        onConfirm={submitWhEdit}
        onCancel={() => { if (!rowBusy) setWhEditConfirm(false); }}
      />

      <SuccessDialog
        visible={rowSuccess !== null}
        title="Done"
        message={rowSuccess ?? undefined}
        primaryAction={{ label: 'Done', onPress: () => setRowSuccess(null) }}
        onClose={() => setRowSuccess(null)}
      />
    </ScreenShell>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: Colors.paper },
  pageHead: {
    flexDirection: 'row', alignItems: 'flex-start', gap: 16,
    paddingHorizontal: 28, paddingTop: 20, paddingBottom: 18,
    borderBottomWidth: 1, borderBottomColor: Colors.line,
    backgroundColor: Colors.card, flexShrink: 0,
  },
  pageTitle: { fontSize: 20, fontWeight: FontWeight.semibold, color: Colors.ink, letterSpacing: -0.2 },
  pageSub: { fontSize: 13, color: Colors.ink3, marginTop: 4 },
  body: { flex: 1 },
  bodyContent: { maxWidth: 840, alignSelf: 'center', width: '100%' as any, padding: 24 },
  sectionH: { marginBottom: 4 },
  sectionHRow: { flexDirection: 'row', alignItems: 'center', gap: 9 },
  sectionIcon: {
    width: 28, height: 28, borderRadius: Radii.r2,
    backgroundColor: Colors.accentSoft, borderWidth: 1, borderColor: Colors.accentLine,
    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  sectionTitle: { fontSize: 15, fontWeight: FontWeight.bold, color: Colors.ink, letterSpacing: -0.2 },
  sectionSub: { fontSize: 12.5, color: Colors.ink3, marginTop: 4, marginLeft: 37 },
  justCreatedBanner: {
    flexDirection: 'row', alignItems: 'flex-start', gap: 12,
    padding: 16, marginBottom: 16,
    backgroundColor: Colors.warnSoft,
    borderWidth: 1, borderColor: Colors.warnLine,
    borderLeftWidth: 3, borderLeftColor: Colors.warn,
    borderRadius: Radii.r4,
    ...(Shadow.sm as object),
  },
  justCreatedTitle: { fontSize: 13, fontWeight: FontWeight.semibold, color: Colors.warn, marginBottom: 8 },
  justCreatedTokenRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  justCreatedToken: {
    flex: 1, fontSize: 12, color: Colors.ink,
    backgroundColor: Colors.card, padding: '6px 10px' as any,
    paddingHorizontal: 10, paddingVertical: 6,
    borderRadius: Radii.r2, borderWidth: 1, borderColor: Colors.warnLine,
  },
  card: {
    backgroundColor: Colors.card, borderRadius: Radii.r4,
    borderWidth: 1, borderColor: Colors.line,
    borderTopWidth: 3, borderTopColor: Colors.accent,
    marginBottom: 16, overflow: 'hidden',
    ...(Shadow.md as object),
  },
  tableRow: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 22, paddingVertical: 20, ...(Platform.OS === 'web' ? { transitionProperty: 'background-color, box-shadow', transitionDuration: '150ms', transitionTimingFunction: 'cubic-bezier(.2,.7,.3,1)' } as any : {}) },
  rowHover: { backgroundColor: Colors.paper2, ...(Platform.OS === 'web' ? { boxShadow: `inset 3px 0 0 ${Colors.accent}` } as any : {}) },
  tableHeader: { backgroundColor: Colors.paper2, borderBottomWidth: 1, borderBottomColor: Colors.line, paddingVertical: 12 },
  thText: { fontSize: 11.5, fontWeight: FontWeight.semibold, color: Colors.ink3 },
  rowBorder: { borderBottomWidth: 1, borderBottomColor: Colors.line },
  cellText: { fontSize: 13, color: Colors.ink },
  dialogInput: {
    width: '100%', marginTop: 6,
    borderWidth: 1, borderColor: Colors.lineStrong,
    borderRadius: Radii.r3, paddingHorizontal: 12, paddingVertical: 10,
    fontSize: FontSize.md, color: Colors.ink, backgroundColor: Colors.card,
    ...(Platform.OS === 'web' ? { outlineStyle: 'none', boxShadow: 'inset 0 1px 2px rgba(16,24,40,0.04)' } as any : {}),
  },
  tokenText: {
    fontSize: 12, color: Colors.ink2, alignSelf: 'flex-start',
    backgroundColor: Colors.paper2, paddingHorizontal: 9, paddingVertical: 4, borderRadius: Radii.r2,
    borderWidth: 1, borderColor: Colors.line,
    fontFamily: Platform.OS === 'web' ? 'monospace' : undefined, letterSpacing: 0.3,
  },
  metaText: { fontSize: 12, color: Colors.ink3 },
  tokenActions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 4 },
  iconBtn: {
    width: 28, height: 28, justifyContent: 'center', alignItems: 'center',
    borderRadius: Radii.r1,
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } as any : {}),
  },
  tableFooter: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 16,
    paddingHorizontal: 22, paddingVertical: 16,
    backgroundColor: Colors.paper2,
    borderTopWidth: 1, borderTopColor: Colors.line,
  },
  footerHint: { flex: 1, fontSize: 12, color: Colors.ink3, lineHeight: 17 },
  webhookRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 12, padding: 16 },
  webhookTitleRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 4 },
  webhookUrl: { fontSize: 12.5, color: Colors.ink, flex: 1 },
  webhookEvents: { flexDirection: 'row', flexWrap: 'wrap', gap: 4, marginBottom: 4 },
  lastDelivery: { fontSize: 11.5, color: Colors.ink3 },
  monoText: { fontSize: 11.5 },
  webhookBtns: { flexDirection: 'row', gap: 6, alignItems: 'center' },
  addWebhookRow: {
    flexDirection: 'row', gap: 8, padding: 16,
    backgroundColor: Colors.paper2,
    borderTopWidth: 1, borderTopColor: Colors.line,
  },
  emptyMini: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 10, paddingVertical: 28, paddingHorizontal: 16 },
  emptyMiniIcon: {
    width: 32, height: 32, borderRadius: Radii.full,
    backgroundColor: Colors.accentSoft, borderWidth: 1, borderColor: Colors.accentLine,
    alignItems: 'center', justifyContent: 'center',
  },
  emptyMiniText: { fontSize: 13, color: Colors.ink3 },
  signingRow: { flexDirection: 'row', alignItems: 'center', gap: 12, padding: 16 },
  signingToken: {
    flex: 1, fontSize: 12.5, color: Colors.ink2, letterSpacing: 0.4,
    backgroundColor: Colors.paper2, paddingHorizontal: 14, paddingVertical: 11,
    borderRadius: Radii.r3, borderWidth: 1, borderColor: Colors.line,
    fontFamily: Platform.OS === 'web' ? 'monospace' : undefined,
    ...(Platform.OS === 'web' ? { boxShadow: 'inset 0 1px 2px rgba(16,24,40,0.04)' } as any : {}),
  },
  // Modal
  overlay: {
    position: 'absolute' as any, top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(16,24,40,0.42)',
    justifyContent: 'center', alignItems: 'center',
    zIndex: 100, padding: 24,
  },
  modal: {
    width: 520, maxWidth: '100%', backgroundColor: Colors.card,
    borderRadius: Radii.r4, overflow: 'hidden',
  },
  modalHead: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    padding: 18, borderBottomWidth: 1, borderBottomColor: Colors.line,
  },
  modalTitle: { fontSize: 18, fontWeight: FontWeight.semibold, color: Colors.ink },
  modalBody: { padding: 20, gap: 16 },
  modalFoot: {
    flexDirection: 'row', justifyContent: 'flex-end', gap: 8,
    padding: 14, borderTopWidth: 1, borderTopColor: Colors.line,
    backgroundColor: Colors.paper2,
  },
  field: { gap: 4 },
  fieldLabel: { fontSize: 13, fontWeight: FontWeight.medium, color: Colors.ink },
  fieldHint: { fontSize: 12, color: Colors.ink3 },
  input: {
    borderWidth: 1, borderColor: Colors.line,
    borderRadius: Radii.r2, paddingHorizontal: 10, paddingVertical: 7,
    fontSize: 13.5, color: Colors.ink, backgroundColor: Colors.card,
  },
  pickerRow: { flexDirection: 'row', gap: 8 },
  pickerCard: {
    flex: 1, padding: 12, borderRadius: Radii.r3,
    borderWidth: 1.5, borderColor: Colors.line,
    backgroundColor: Colors.paper2,
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } as any : {}),
  },
  pickerCardActive: { borderColor: Colors.accent, backgroundColor: Colors.accentSoft },
  pickerTitle: { fontSize: 13, fontWeight: FontWeight.semibold, color: Colors.ink },
  pickerTitleActive: { color: Colors.accentInk },
  pickerSub: { fontSize: 11.5, color: Colors.ink3, marginTop: 2 },
  warnBox: {
    flexDirection: 'row', gap: 8, alignItems: 'flex-start',
    padding: '10px 12px' as any,
    paddingHorizontal: 12, paddingVertical: 10,
    backgroundColor: Colors.warnSoft,
    borderWidth: 1, borderColor: Colors.warnLine,
    borderRadius: Radii.r2,
  },
  warnText: { flex: 1, fontSize: 12, color: Colors.ink2 },
});
